import { useMemo } from 'react'
import { useLiveIdToken } from '~/src/lib/firebase/useLiveIdToken'

/**
 * Who is asking, for an api call that decides per viewer (the brain, the fragrance cards):
 * `x-user-id`, `x-authorization` and `x-temp-user-id`. Shared by the chat and the fragrance house
 * pages so the token rule lives in one place. The caller passes its own user and temp id, so the
 * chat keeps one source for the temp id it also uses for thread ownership.
 *
 * ⛔ `liveIdToken`, not `authUser.accessToken` alone: that is the page-load token, dead after an
 * hour, and the api then quietly drops staff-only tools.
 */
export default function useViewerAuthHeaders({
  authUser,
  tempUserId,
}: {
  authUser: { uid?: string; accessToken?: string } | null | undefined
  tempUserId: unknown
}): Readonly<Record<string, string>> {
  const liveIdToken = useLiveIdToken()
  const uid = authUser?.uid
  const accessToken = authUser?.accessToken
  return useMemo(() => {
    const h: Record<string, string> = {}
    if (uid) h['x-user-id'] = uid
    const idToken =
      liveIdToken && liveIdToken.uid === uid ? liveIdToken.token : accessToken
    if (idToken) h['x-authorization'] = idToken
    if (tempUserId) h['x-temp-user-id'] = tempUserId as string
    return h
  }, [uid, accessToken, liveIdToken, tempUserId])
}
